import { useCallback, useContext, useEffect, useState } from 'react';

// import Sentry SDK directly since this is published package
import * as Sentry from '@sentry/browser';

import { getCookie } from '@atlassian/browser-storage-controls';

import { AppDataContext } from 'src/app/data';

import urls from '../urls';

// user prefs call expects curlies around the uuid
export const ensureCurlyBrackets = (uuid: string): string => {
  // do nothing and return
  if (!uuid) return uuid;

  // if already has curly brackets, return as-is
  if (uuid.startsWith('{') && uuid.endsWith('}')) {
    return uuid;
  }

  // add curly brackets
  return `{${uuid}}`;
};

/**
 * Optional callback functions for handling user preference update results.
 * Used with usePref hook to execute custom logic after preference updates complete.
 */
type OptionalCallbacks = {
  onUpdateSuccess?: () => void;
  onUpdateFailed?: () => void;
};

type UpdatePrefHandler = (
  value: string,
  optionalCallbacks?: OptionalCallbacks
) => undefined;

export const usePref: (
  prefKey: string,
  skipPrefCall: boolean
) => [string, UpdatePrefHandler, boolean] = (prefKey, skipPrefCall) => {
  const { user } = useContext(AppDataContext);
  const userUuid = ensureCurlyBrackets(user?.uuid || '');
  const [isLoading, setIsLoading] = useState(true);
  const [prefValue, setPrefValue] = useState<string>('');

  useEffect(() => {
    if (!userUuid || skipPrefCall) return undefined;

    fetch(
      urls.api.internal.userPreference(
        userUuid,
        encodeURIComponent(prefKey.toString())
      )
    )
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          if (response.status === 404) {
            // 404s are returned when no value has been set for a user/pref combination
            return Promise.resolve('');
          }
        }
        return Promise.reject(
          new Error(`Error resolving user pref in global nav: ${prefKey}`)
        );
      })
      .then((val: string) => {
        setPrefValue(val);
      })
      .catch(error => {
        Sentry.captureException(error);
      })
      .finally(() => {
        setIsLoading(false);
      });

    return undefined;
  }, [userUuid, prefKey, skipPrefCall]);

  const updatePref = useCallback(
    (value: string, optionalCallbacks?: OptionalCallbacks) => {
      if (!userUuid) return undefined;
      const token = getCookie('csrftoken');

      setIsLoading(true);
      fetch(
        urls.api.internal.userPreference(
          userUuid,
          encodeURIComponent(prefKey.toString())
        ),
        {
          method: 'PUT',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRFToken': token || '',
          },
          body: JSON.stringify({ value }),
        }
      )
        .then(response => {
          if (response.ok) {
            return response.json();
          } else {
            return Promise.reject(
              new Error(`Error updating user pref in global nav: ${prefKey}`)
            );
          }
        })
        .then(response => {
          setPrefValue(response.value);
          if (optionalCallbacks?.onUpdateSuccess) {
            optionalCallbacks?.onUpdateSuccess();
          }
        })
        .catch(error => {
          Sentry.captureException(error);
          if (optionalCallbacks?.onUpdateFailed) {
            optionalCallbacks?.onUpdateFailed();
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
      return undefined;
    },
    [userUuid, prefKey]
  );

  return [prefValue, updatePref, isLoading];
};
